import { isClientSide } from 'src/utils/constants';
import { safeJSONParse } from 'src/utils/cookieHelper';
import {
  getEnvVariable,
  NEXT_PUBLIC_ENV,
  NEXT_PUBLIC_ENV_VARIANT,
  NEXT_PUBLIC_REDIS_HOST
} from 'src/utils/envVariablesHelper';
import { GetServerSidePropsResult } from 'next';
import { AxiosResponse } from 'axios';
import logger from 'src/utils/logger';
import { getAPIDomain } from '../constants';
import { APIError } from '../types';

const getCachedData = async <T>(
  redisClient,
  key: string
): Promise<T | null> => {
  const result = await redisClient?.get(key);
  if (result) return safeJSONParse(result);
  return null;
};

const setCacheValue = async <T>(
  redisClient,
  key: string,
  fetchedData: T
): Promise<T> => {
  await redisClient?.set(
    key,
    JSON.stringify(fetchedData),
    'EX',
    // 3 hours = 10800 second. Cache expiration set for 3 hours
    10800
  );
  return fetchedData;
};

export const buildCacheKey = (requestConfig) => {
  const { url, method, data, isPage } = requestConfig;
  const urlWithDomain = url.replace(getAPIDomain(), '');
  let finalUrl = urlWithDomain;
  if (urlWithDomain?.includes('?')) {
    const splittedUrl = urlWithDomain.split(/\?(.*)/s);
    finalUrl = `${splittedUrl[0]}:${splittedUrl[1]}`;
  }
  return `${isPage ? 'page:' : ''}${method}:${finalUrl}${
    data ? `:${JSON.stringify(data).replaceAll(':', '=')}` : ''
  }`;
};

type Cache =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | GetServerSidePropsResult<{ [key: string]: any }>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | AxiosResponse<any, any>
  | APIError;

export const fetchCache = async <T extends Cache>(
  requestConfig,
  fetchData: () => Promise<T>
): Promise<T> => {
  try {
    // Do not use Redis in case of development environment if NEXT_PUBLIC_REDIS_HOST is not provided
    const isRedisDisabled =
      getEnvVariable(NEXT_PUBLIC_ENV) === NEXT_PUBLIC_ENV_VARIANT.DEV &&
      !getEnvVariable(NEXT_PUBLIC_REDIS_HOST);

    if (!isClientSide && !isRedisDisabled) {
      const redisClient = await import('./redisClient');
      if (redisClient) {
        const key = buildCacheKey(requestConfig);
        const cachedData = (await getCachedData(
          redisClient?.default,
          key
        )) as Cache;
        if (cachedData) {
          if (
            'props' in cachedData &&
            'hasError' in cachedData.props &&
            !cachedData?.props?.hasError
          ) {
            // Do not return cached value for pages with hasError:true in props
            return fetchData();
          }
          return cachedData as T;
        }
        const fetchedData = await fetchData();
        if (fetchedData) {
          if (
            'props' in fetchedData &&
            'hasError' in fetchedData.props &&
            fetchedData?.props?.hasError
          ) {
            // Do not cache pages with errors
            return fetchedData;
          }
          return setCacheValue(redisClient?.default, key, fetchedData);
        }
        // Bad fetched data. Return null
        return null;
      }
    }
    return fetchData();
  } catch (error) {
    logger.log('fetchCache failed', error);
    return fetchData();
  }
};
